import { initials } from '@/lib/utils/formatters';

/**
 * Avatar colours — the design system's categorical `entity-*` ramp, which is
 * the sanctioned palette for "any set of unordered entities that need to be
 * told apart". Each slot is read through its `avatar-*` pair in `globals.css`:
 * the fill plus the text colour that reaches WCAG AA (4.5:1) on it, white on
 * the darker slots and dark ink on the lighter ones, retuned for dark mode.
 *
 * The ramp is validated colourblind-safe **in slot order**, so re-ordering
 * this list silently degrades that — and it changes every existing avatar's
 * colour, since the slot is picked by index.
 */
export const AVATAR_PALETTE: Array<{ bg: string; fg: string }> = [
  { bg: 'bg-avatar-1', fg: 'text-avatar-fg-1' },
  { bg: 'bg-avatar-2', fg: 'text-avatar-fg-2' },
  { bg: 'bg-avatar-3', fg: 'text-avatar-fg-3' },
  { bg: 'bg-avatar-4', fg: 'text-avatar-fg-4' },
  { bg: 'bg-avatar-5', fg: 'text-avatar-fg-5' },
  { bg: 'bg-avatar-6', fg: 'text-avatar-fg-6' },
  { bg: 'bg-avatar-7', fg: 'text-avatar-fg-7' },
  { bg: 'bg-avatar-8', fg: 'text-avatar-fg-8' },
  { bg: 'bg-avatar-9', fg: 'text-avatar-fg-9' },
  { bg: 'bg-avatar-10', fg: 'text-avatar-fg-10' },
];

/**
 * A person's initials, by the same two-letter rule as every other avatar:
 * first + last name gives one letter of each; a single name gives its first
 * two letters. Without a name, the email's local part stands in.
 */
export function getInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  email?: string | null,
): string {
  const first = firstName?.trim();
  const last = lastName?.trim();
  // A multi-word first name ("Anna Maria") must not push the surname out.
  if (first && last) return `${[...first][0]}${[...last][0]}`.toUpperCase();
  return initials(first || last || email?.split('@')[0]);
}

/** A company's initials — the shared two-letter rule from `initials`. */
export function getCompanyInitials(name: string): string {
  return initials(name);
}

export function avatarColor(seed: string): { bg: string; fg: string } {
  let hash = 0;
  for (let i = 0; i < seed.length; i++)
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]!;
}

/** Returns a combined Tailwind bg+text class string for an avatar, derived from a seed string */
export function avatarColorClass(seed: string): string {
  const { bg, fg } = avatarColor(seed);
  return `${bg} ${fg}`;
}

export function imgUrl(
  url: string | null | undefined,
  size: number,
): string | undefined {
  if (!url) return undefined;
  return `${url}?fit=crop&w=${size}&h=${size}&auto=format`;
}
